import { CODE_EDITOR_SUPPORTED_LANG } from '@components/CodeEditor/types';
import ConnectorTest from '@components/ConnectorEdit/ConnectorTest';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import DynamicValueHelperText from '@components/Tooltips/DynamicValuesHelperText';
import {
	Alert,
	FormControl,
	Grid,
	InputLabel,
	Typography,
	useTheme
} from '@mui/material';
import { ITheme } from 'types/theme';
import {
	getConnectorProtocol,
	getSelectedConnectorId,
	getServiceId,
	getServiceTypeValues,
	isCustomProvider,
	onConnectorSelect
} from '@utils/connectors';
import { CodeEditorInput } from 'components/CodeEditor/CodeEditorInput';
import { AuthMethodRoute } from 'constants/routes';
import { MessageConnectorContext } from 'hooks/useConnectorsContext';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import {
	ConnectorIndexEnum,
	ConnectorTypeEnum,
	CustomAuthConnector,
	EmailConnectorTypes,
	EMAIL_CONNECTOR_SELECTOR_ID,
	IDefaultConnectorProps,
	IFormikValues,
	ProviderPrefix,
	SmsConnectorTypes,
	SMS_CONNECTOR_SELECTOR_ID
} from 'types/connectors';
import HelpTooltip from '@components/Tooltips/HelpTooltip';
import { useBoolean } from 'usehooks-ts';
import ConnectorSelect from '../../ConnectorEdit/ConnectorSelect';
import { FORM_KEYS } from '../CommonFormKeys';
import {
	BodyValueEntry,
	getConnectorsDisabledTemplateAlert,
	getDynamicValuesTooltip,
	getEmailBodyInputValues,
	getFieldDynamicKey,
	getTextBodyInputValues,
	IAlertItemsProps
} from './ConnectorFormUtils';

interface IConnectorFormProps {
	providerType: ProviderPrefix;
	pageId: AuthMethodRoute;
	formik: IFormikValues;
	templates?: IDefaultConnectorProps | undefined;
	disabled?: boolean;
	title?: string;
	titleTooltip?: string;
	customMethodSelectorLabel?: string;
	enableCustomMethod?: boolean;
	showCustomMethodAlert?: boolean;
	customMethodSelectorAlert?: string;
	emailResetMethod?: AuthMethodRoute;
}

interface IConnectorFormInnerProps {
	connectorSelectId: string;
	serviceProviderFieldName: string;
	bodyKey: string;
	bodyValue: BodyValueEntry[];
	connectors: CustomAuthConnector[];
	providerId: string;
	alertProps: Omit<IAlertItemsProps, 'onClose'>;
}

let connectorProps: IConnectorFormInnerProps = {
	connectorSelectId: '',
	serviceProviderFieldName: '',
	bodyKey: '',
	bodyValue: [],
	connectors: [],
	providerId: '',
	alertProps: { message: '', items: [] }
};

const ConnectorForm: FC<IConnectorFormProps> = ({
	providerType,
	pageId,
	formik,
	templates,
	disabled,
	title,
	titleTooltip,
	customMethodSelectorAlert,
	showCustomMethodAlert,
	emailResetMethod
}) => {
	const { tr } = intl;
	const theme = useTheme<ITheme>();
	const [showTemplateAlert, setShowTemplateAlert] = useState(false);

	const {
		emailConnectors,
		smsConnectors,
		onTestMessageService,
		createConnector,
		getCustomTemplates
	} = useContext(MessageConnectorContext);

	const createNewConnector = useCallback(
		(type: ConnectorIndexEnum) => createConnector(type, formik.values),
		[createConnector, formik.values]
	);

	const alertItem = useCallback(
		(type: ConnectorIndexEnum) => ({
			key: type,
			label: `connectors.template.info.create.${type}`,
			callback: () => createNewConnector(type)
		}),
		[createNewConnector]
	);

	const getAlertProps = useCallback(
		(items: ConnectorIndexEnum[]) => ({
			message: `connectors.template.info.${providerType}`,
			items: items.map(alertItem)
		}),
		[alertItem, providerType]
	);

	const isEmailProvider = providerType === ProviderPrefix.EMAIL;
	const isSmsProvider = providerType === ProviderPrefix.SMS;

	const providerTypeFullname = isEmailProvider
		? ConnectorTypeEnum.EMAIL_PROVIDERS
		: ConnectorTypeEnum.SMS_PROVIDERS;

	switch (providerType) {
		case ProviderPrefix.EMAIL:
			connectorProps = {
				connectorSelectId: EMAIL_CONNECTOR_SELECTOR_ID,
				serviceProviderFieldName: FORM_KEYS.emailServiceProvider,
				bodyKey: FORM_KEYS.emailBody,
				bodyValue: getEmailBodyInputValues(
					formik.values,
					getCustomTemplates(
						ConnectorTypeEnum.EMAIL_PROVIDERS,
						emailResetMethod
					)
				),
				connectors: emailConnectors(),
				providerId: formik.values.emailServiceProvider,
				alertProps: getAlertProps(EmailConnectorTypes)
			};
			break;
		case ProviderPrefix.SMS:
			connectorProps = {
				connectorSelectId: SMS_CONNECTOR_SELECTOR_ID,
				serviceProviderFieldName: FORM_KEYS.textServiceProvider,
				bodyKey: FORM_KEYS.textMessageBody,
				bodyValue: getTextBodyInputValues(formik.values),
				connectors: smsConnectors(),
				providerId: formik.values.textServiceProvider,
				alertProps: getAlertProps(SmsConnectorTypes)
			};
			break;
		default:
			throw new Error('Connector type not defined');
	}

	const {
		connectorSelectId,
		serviceProviderFieldName,
		bodyKey,
		bodyValue,
		connectors,
		providerId,
		alertProps
	} = connectorProps;

	const selectedProvider = getSelectedConnectorId(providerId);
	const isCustomService = isCustomProvider(selectedProvider, connectors);
	const {
		value: fadeInAlert,
		setFalse: unsetFadeInAlert,
		setTrue: setFadeInAlert
	} = useBoolean(true);

	const CONNECTOR_ALERT_VISIBLE_DURATION = 10000;

	const showAlertIfNeeded = () => {
		if (!disabled && !isCustomService && !showTemplateAlert) {
			setFadeInAlert();
			setShowTemplateAlert(true);
			setTimeout(() => {
				unsetFadeInAlert();
				setTimeout(() => {
					setShowTemplateAlert(false);
				}, 250);
			}, CONNECTOR_ALERT_VISIBLE_DURATION);
		}
	};

	const editDisabledTemplateAlert = useMemo(
		() =>
			showTemplateAlert && (
				<Grid item xs={12}>
					{getConnectorsDisabledTemplateAlert({
						...alertProps,
						fadeInAlert,
						onClose: () => setShowTemplateAlert(false)
					})}
				</Grid>
			),
		[showTemplateAlert, alertProps, fadeInAlert]
	);

	const connectorTestUtil = isCustomService && (
		<Grid item xs={12}>
			<ConnectorTest
				type={providerTypeFullname}
				gridSpacing={2}
				onSubmit={async (target, onComplete, onFail) => {
					await onTestMessageService({
						isAdhoc: false,
						providerType,
						providerId,
						target,
						onComplete,
						onFail,
						pageId,
						providerProtocol: getConnectorProtocol(providerId),
						values: getServiceTypeValues(providerType, formik.values)
					});
				}}
			/>
		</Grid>
	);

	const dynamicValuesHelpText = useMemo(
		() =>
			isCustomService && (
				<Grid item xs={12}>
					<DynamicValueHelperText
						{...getDynamicValuesTooltip({
							tr,
							pageId: emailResetMethod || pageId,
							providerType
						})}
					/>
				</Grid>
			),
		[pageId, providerType, isCustomService, tr, emailResetMethod]
	);

	const emailSubjectInput = isEmailProvider && (
		<Grid item xs={12}>
			<TextFieldValidation
				id={getFieldDynamicKey(FORM_KEYS.emailSubject, selectedProvider)}
				disabled={!isCustomService}
				label={tr(`authMethods.fields.${FORM_KEYS.emailSubject}`)}
				formik={formik}
				fullWidth
			/>
		</Grid>
	);

	const emailBodyInput = templates && isEmailProvider && emailConnectors() && (
		<CodeEditorInput
			id={getFieldDynamicKey(bodyKey, selectedProvider)}
			editable={isCustomService}
			label={tr(`authMethods.fields.${bodyKey}`)}
			onChange={formik.setFieldValue}
			values={bodyValue}
			lang={CODE_EDITOR_SUPPORTED_LANG.html}
		/>
	);

	const smsBodyInput = isSmsProvider && (
		<TextFieldValidation
			id={getFieldDynamicKey(FORM_KEYS.textMessageBody, selectedProvider)}
			formik={formik}
			disabled={!isCustomService}
			label={intl.tr(`authMethods.fields.${FORM_KEYS.textMessageBody}`)}
			fullWidth
			multiline
			rows="6"
		/>
	);

	const bodyEditInput = (
		<Grid item xs={12} onClick={showAlertIfNeeded}>
			{smsBodyInput}
			{emailBodyInput}
		</Grid>
	);

	const emailCustomTemplates = getCustomTemplates(
		ConnectorTypeEnum.EMAIL_PROVIDERS,
		emailResetMethod
	);

	const smsCustomTemplates = getCustomTemplates(
		ConnectorTypeEnum.SMS_PROVIDERS
	);

	const onSelect = useCallback(
		async (type: string, id: string, resetMethodName?: AuthMethodRoute) => {
			setShowTemplateAlert(false);
			onConnectorSelect({
				keys: FORM_KEYS,
				form: formik,
				defaultTemplates: templates,
				emailCustomTemplates,
				smsCustomTemplates,
				resetMethod: resetMethodName
			})(type, id);
		},
		[emailCustomTemplates, smsCustomTemplates, templates, formik]
	);

	const connectorSelector = (
		<Grid item xs={12}>
			<FormControl fullWidth>
				<InputLabel
					id={`${isEmailProvider ? 'email' : 'sms'}-connector-selector`}
				>
					{tr(`authMethods.fields.${serviceProviderFieldName}`)}
				</InputLabel>
				<ConnectorSelect
					id={connectorSelectId}
					labelId={`${isEmailProvider ? 'email' : 'sms'}-connector-selector`}
					connectors={connectors}
					connectorType={providerTypeFullname}
					selectedValue={selectedProvider}
					formik={formik}
					disabled={disabled}
					onSelect={async (connector: CustomAuthConnector) => {
						const { common, providerProtocol } = connector || {};
						await onSelect(
							serviceProviderFieldName,
							getServiceId(providerProtocol, common?.id),
							emailResetMethod
						);
					}}
				/>
			</FormControl>
		</Grid>
	);

	// feature currently disabled in production
	/*
	const customMethodSelector = emailResetMethod && (
		<Grid item xs={12} data-testid="custom-method-selector">
			<FormControl fullWidth>
				<InputLabel id="custom-method-select">
					{tr(
						customMethodSelectorLabel ||
							'pages.authMethods.password.reset.methods.default'
					)}
				</InputLabel>
				<Select
					id={FORM_KEYS.resetAuthMethod}
					labelId="custom-method-select"
					value={emailResetMethod}
					label={tr(
						customMethodSelectorLabel || 'pages.authMethods.method.title'
					)}
					onChange={(e) => {
						formik.setFieldValue(FORM_KEYS.resetAuthMethod, e.target.value);
						formik.setFieldValue(
							FORM_KEYS.emailServiceProvider,
							DEFAULT_CONNECTOR_ID
						);
						formik.setFieldValue(
							FORM_KEYS.emailSubject,
							(templates as any)[e.target.value].emailSubject
						);
						formik.setFieldValue(
							FORM_KEYS.emailBody,
							(templates as any)[e.target.value].emailBody
						);
					}}
				>
					<MenuItem value="enchantedlink" key="enchantedlink">
						{intl.tr('pages.authMethods.password.reset.method.enchantedlink')}
					</MenuItem>
					<MenuItem value="magiclink" key="magiclink">
						{intl.tr('pages.authMethods.password.reset.method.magiclink')}
					</MenuItem>
				</Select>
			</FormControl>
		</Grid>
	);
	*/

	const disabledColor = theme.palette.action.disabled;
	const sectionTitle = useMemo(
		() => (
			<Grid container mt={3} ml={2}>
				<Grid item>
					<Typography
						variant="subtitle1"
						color={disabled ? disabledColor : ''}
						sx={{ textTransform: 'uppercase' }}
					>
						{tr(title || `pages.authMethods.section.${providerType}.title`)}
					</Typography>
				</Grid>
				{titleTooltip && (
					<Grid item ml={1}>
						<HelpTooltip title={intl.tr(titleTooltip)} placement="right" />
					</Grid>
				)}
			</Grid>
		),
		[providerType, tr, disabled, disabledColor, title, titleTooltip]
	);

	return (
		<Grid container>
			<Grid item mt={2} container spacing={2} xs={8}>
				{sectionTitle}
				{/* feature currently disabled in production */}
				{/* {enableCustomMethod && customMethodSelector} */}
				{showCustomMethodAlert && customMethodSelectorAlert && (
					<Grid item xs={12}>
						<Alert severity="warning">
							{intl.tr(customMethodSelectorAlert)}
						</Alert>
					</Grid>
				)}

				<Grid item xs={12}>
					{connectorSelector}
				</Grid>
				{editDisabledTemplateAlert}
				{emailSubjectInput}
				{bodyEditInput}
				{dynamicValuesHelpText}
				{connectorTestUtil}
			</Grid>
		</Grid>
	);
};

ConnectorForm.defaultProps = {
	templates: undefined,
	disabled: false,
	title: undefined,
	titleTooltip: undefined,
	customMethodSelectorLabel: undefined,
	enableCustomMethod: undefined,
	showCustomMethodAlert: undefined,
	customMethodSelectorAlert: undefined,
	emailResetMethod: undefined
};

export default ConnectorForm;
